import { ConsentDto, LanguageDto, useDialog } from '@ecdlink/core';
import {
  ActionModal,
  BannerWrapper,
  Button,
  DialogPosition,
  renderIcon,
  Typography,
} from '@ecdlink/ui';
import { IonContent } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useAppDispatch } from '@store';
import {
  contentConsentSelectors,
  contentConsentThunkActions,
} from '@store/content/consent';
import LanguageSelector from '../language-selector/language-selector';
import * as styles from './article.styles';
import { ArticleProps } from './article.types';
import { LanguageCode } from '@/i18n/types';
import { useTenant } from '@/hooks/useTenant';

export const Article = ({
  visible = true,
  consentEnumType,
  title,
  onClose,
  showClose = true,
  isOpen = false,
  isFromRegistration,
  isConsentScreen,
}: ArticleProps) => {
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();
  const [articleText, setArticleText] = useState<string>('');
  const [articleImage, setArticleImage] = useState<string>('');
  const [language] = useState({ locale: 'en-za' });
  const [availableLanguages, setAvailableLanguages] = useState([
    language.locale as LanguageCode,
  ]);
  const [languageOptions, setLanguageOptions] = useState<LanguageDto[]>([]);
  const consent = useSelector(contentConsentSelectors.getConsentSelector);
  const dialog = useDialog();
  const tenant = useTenant();

  useEffect(() => {
    if (consent && visible && !isOpen) {
      getContent(consent, language.locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent, visible, isOpen]);

  useEffect(() => {
    getOpenContent('en-za');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, consentEnumType]);

  const changeLanugage = async (language: LanguageDto) => {
    await getOpenContent(language.locale);
  };

  const getOpenContent = async (locale: string) => {
    let content = [];
    if (isOnline) {
      content = await appDispatch(
        contentConsentThunkActions.getOpenConsent({
          locale: locale,
          name: consentEnumType,
        })
      ).unwrap();
    } else {
      content =
        consent?.filter(
          (x) => x.name === consentEnumType && x.locale === locale
        ) ?? [];
    }

    if (content && content.length > 0) {
      const consentFilter = content?.[0];
      var description = consentFilter?.description ?? '';
      setArticleImage(consentFilter?.image ?? '');

      if (!consentFilter || description.length === 0) {
        presentUnavailableAlert();
      }

      if (
        description.length > 0 &&
        (description.indexOf('[organisationName]') !== -1 ||
          description.indexOf('[applicationName]') !== -1)
      ) {
        const replacedOrgName = description.replaceAll(
          '[organisationName]',
          tenant.tenant?.organisationName!
        );

        const replacedAppName = replacedOrgName.replaceAll(
          '[applicationName]',
          tenant.tenant?.applicationName!
        );

        setArticleText(replacedAppName ?? '');
      } else {
        setArticleText(description);
      }

      if (consentFilter?.availableLanguages !== undefined) {
        const langs = consentFilter.availableLanguages ?? [];
        setLanguageOptions(langs);
        setAvailableLanguages(
          langs.length
            ? langs.map((item) => item?.locale as LanguageCode)
            : [language.locale as LanguageCode]
        );
      }
    } else {
      setArticleText('');
      presentUnavailableAlert();
    }
  };

  const getContent = async (
    consentList: ConsentDto[] | undefined,
    locale: string
  ) => {
    const consentFilter = consentList?.find(
      (x) => x.name === consentEnumType && x.locale === locale
    );
    var description = consentFilter?.description ?? '';
    setArticleImage(consentFilter?.image ?? '');

    if (!consentFilter || description.length === 0) {
      presentUnavailableAlert();
    }

    if (
      description.length > 0 &&
      (description.indexOf('[organisationName]') !== -1 ||
        description.indexOf('[applicationName]') !== -1)
    ) {
      const replacedOrgName = description.replaceAll(
        '[organisationName]',
        tenant.tenant?.organisationName!
      );

      const replacedAppName = replacedOrgName.replaceAll(
        '[applicationName]',
        tenant.tenant?.applicationName!
      );

      setArticleText(replacedAppName ?? '');
    } else {
      setArticleText(description);
    }

    if (consentFilter?.availableLanguages !== undefined) {
      const langs = consentFilter.availableLanguages ?? [];
      setLanguageOptions(langs);
      setAvailableLanguages(
        langs.length
          ? langs.map((item) => item?.locale as LanguageCode)
          : [language.locale as LanguageCode]
      );
    }
  };

  const presentUnavailableAlert = () => {
    dialog({
      position: DialogPosition.Middle,
      render: (submit, close) => {
        return (
          <ActionModal
            className={'bg-white'}
            title="No content found"
            detailText={
              'Could not find any content for the selected language, please select another.'
            }
            icon={'InformationCircleIcon'}
            iconColor={'infoDark'}
            iconBorderColor={'infoBb'}
            actionButtons={[
              {
                text: 'Close',
                colour: 'primary',
                onClick: close,
                type: 'filled',
                textColour: 'white',
                leadingIcon: 'XIcon',
              },
            ]}
          />
        );
      },
    });
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {visible && (
        <div className={styles.contentWrapper}>
          <IonContent scrollY={true}>
            <BannerWrapper
              size={'normal'}
              renderBorder={true}
              showBackground={false}
              color={'primary'}
              onBack={onClose}
              title={title}
              className={styles.bannerContentWrapper}
              backgroundColour={'white'}
              displayOffline={!isOnline}
            >
              <div className={styles.localeDropDownWrapper}>
                <LanguageSelector
                  labelClassName="text-textDark mr-2"
                  currentLocale="en-za"
                  selectLanguage={(data) => changeLanugage(data)}
                  availableLanguages={availableLanguages}
                  languageOptions={languageOptions}
                  notLogged={true}
                  isConsentScreen={isConsentScreen}
                />
              </div>
              <div className={styles.articleTextWrapper}>
                <div className="flex items-start">
                  {articleImage && (
                    <div
                      className="mr-3 flex-shrink-0"
                      style={{ width: '48px', height: '48px' }}
                    >
                      <img
                        src={articleImage}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <Typography type={'markdown'} text={articleText} />
                </div>
              </div>

              {showClose && (
                <div className={styles.bottom}>
                  <Button
                    color={'quatenary'}
                    type={'filled'}
                    onClick={onClose}
                    className={styles.closeButton}
                  >
                    {renderIcon('XIcon', 'h-4 w-4 mr-2')}
                    <Typography
                      color={'white'}
                      type={'body'}
                      weight={'bold'}
                      text={'Close'}
                    />
                  </Button>
                </div>
              )}
            </BannerWrapper>
          </IonContent>
        </div>
      )}
    </>
  );
};

export default Article;
